import { useState, useEffect, useRef, useMemo } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import AppSidebar from '@/components/AppSidebar'
import PwaInstallBanner from '@/components/PwaInstallBanner'
import OfflineBanner from '@/components/OfflineBanner'
import ImpersonationBanner from '@/components/admin/ImpersonationBanner'
import AssinaturaNotificacoesBadge from '@/components/AssinaturaNotificacoesBadge'
import { useAuth } from '@/contexts/AuthContext'
import { useCompanySettings } from '@/hooks/useCompanySettings'
import { supabase } from '@/lib/supabase'
import { today } from '@/lib/helpers'
import { Bell, AlertTriangle, FileText, ShieldAlert, Calendar } from 'lucide-react'
import { Link } from 'react-router-dom'

const TITLES: Record<string, string> = {
  '/': 'Dashboard',
  '/obra': 'Dashboard da Obra',
  '/obras': 'Obras',
  '/diario': 'Diário / RDO',
  '/fotos': 'Fotos & Mídia',
  '/producao': 'Produção',
  '/fvs': 'FVS / Qualidade',
  '/pendencias': 'Pendências',
  '/ocorrencias': 'Ocorrências',
  '/planta': 'Planta da Obra',
  '/cronograma': 'Cronograma',
  '/sst': 'Segurança SST',
  '/historico': 'Histórico',
  '/entregas': 'Entrega Unidades',
  '/fvm': 'FVM / Materiais',
  '/concreto': 'Concreto',
  '/revestimentos': 'Revestimentos',
  '/fornecedores': 'Fornecedores',
  '/admin/usuarios': 'Gestão de Usuários',
  '/identidade': 'Identidade da Empresa',
  '/equipe': 'Equipe',
  '/settings': 'Configurações',
  '/superadmin': 'Super Admin',
  '/chat': 'Chat da Obra',
  '/relatorios': 'Relatórios',
}

export interface NotificationCounts {
  pendVencidas: number
  ocorrCriticas: number
  rdosPendentes: boolean
  sstProximas: number
}

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { obraAtiva, company, user } = useAuth()
  const isCliente = String(user?.perfil || '').toLowerCase() === 'cliente'
  const { settings: companySettings } = useCompanySettings()
  const [pendCount, setPendCount] = useState(0)
  const [ocorrCount, setOcorrCount] = useState(0)
  const [notifs, setNotifs] = useState<NotificationCounts>({
    pendVencidas: 0, ocorrCriticas: 0, rdosPendentes: false, sstProximas: 0,
  })
  const [showNotifs, setShowNotifs] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)

  // Apply company branding as CSS variables
  useEffect(() => {
    const root = document.documentElement
    if (companySettings.cor_primaria && companySettings.cor_primaria !== '#1a1a2e') {
      root.style.setProperty('--company-primary', companySettings.cor_primaria)
      root.style.setProperty('--company-secondary', companySettings.cor_secundaria)
      root.style.setProperty('--company-accent', companySettings.cor_acento)
    } else {
      root.style.removeProperty('--company-primary')
      root.style.removeProperty('--company-secondary')
      root.style.removeProperty('--company-accent')
    }
    return () => {
      root.style.removeProperty('--company-primary')
      root.style.removeProperty('--company-secondary')
      root.style.removeProperty('--company-accent')
    }
  }, [companySettings])

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotifs(false)
      }
    }
    if (showNotifs) document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [showNotifs])

  useEffect(() => {
    if (!obraAtiva) return
    const todayStr = today()
    const threeDays = new Date()
    threeDays.setDate(threeDays.getDate() + 3)
    const threeDaysStr = threeDays.toISOString().split('T')[0]

    Promise.all([
      supabase.from('pendencias').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('concluida', false),
      supabase.from('ocorrencias').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('resolvida', false),
      supabase.from('pendencias').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('concluida', false).lt('prazo', todayStr),
      supabase.from('ocorrencias').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('resolvida', false).eq('severidade', 'critica'),
      supabase.from('diario_obra').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('data_registro', todayStr),
      supabase.from('sst_agendamentos').select('id', { count: 'exact', head: true })
        .eq('obra_id', obraAtiva.id).eq('status', 'agendado')
        .gte('data_evento', todayStr).lte('data_evento', threeDaysStr),
    ]).then(([pe, oc, pv, ocCrit, rdoToday, sstProx]) => {
      setPendCount(pe.count || 0)
      setOcorrCount(oc.count || 0)
      setNotifs({
        pendVencidas: pv.count || 0,
        ocorrCriticas: ocCrit.count || 0,
        rdosPendentes: (rdoToday.count || 0) === 0,
        sstProximas: sstProx.count || 0,
      })
    })
  }, [obraAtiva, location.pathname])

  const title = TITLES[location.pathname] || 'Construzilla'
  const totalNotifs = notifs.pendVencidas + notifs.ocorrCriticas + (notifs.rdosPendentes ? 1 : 0) + notifs.sstProximas

  const notifItems: { icon: React.ReactNode; label: string; count: number | string; color: string; route: string }[] = []
  if (notifs.pendVencidas > 0) notifItems.push({ icon: <AlertTriangle className="w-4 h-4" />, label: 'Pendências vencidas', count: notifs.pendVencidas, color: 'hsl(var(--rose))', route: '/pendencias' })
  if (notifs.ocorrCriticas > 0) notifItems.push({ icon: <ShieldAlert className="w-4 h-4" />, label: 'Ocorrências críticas', count: notifs.ocorrCriticas, color: 'hsl(var(--rose))', route: '/ocorrencias' })
  if (notifs.rdosPendentes) notifItems.push({ icon: <FileText className="w-4 h-4" />, label: 'RDO não preenchido hoje', count: '!', color: 'hsl(var(--amber))', route: '/diario' })
  if (notifs.sstProximas > 0) notifItems.push({ icon: <Calendar className="w-4 h-4" />, label: 'Agendamentos SST próximos', count: notifs.sstProximas, color: 'hsl(var(--amber))', route: '/sst' })

  return (
    <div className="flex h-screen overflow-hidden w-full">
      <AppSidebar pendCount={pendCount} ocorrCount={ocorrCount} notifs={notifs} />
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <ImpersonationBanner />
        <OfflineBanner />
        {/* Topbar */}
        <div
          className="h-12 flex-shrink-0 flex items-center px-4 gap-3"
          style={{
            background: 'hsl(var(--bg1))',
            borderBottom: '1px solid hsl(var(--line))',
          }}
        >
          {/* Mobile logo */}
          <Link to="/" className="md:hidden flex items-center gap-1.5 flex-shrink-0 ml-7">
            <img
              src="/construzilla-logo.png?v=6"
              alt="Construzilla"
              style={{ height: 24, background: 'transparent', border: 'none', borderRadius: 0, boxShadow: 'none', padding: 0 }}
              onError={(e) => {
                e.currentTarget.style.display = 'none'
                const sibling = e.currentTarget.nextElementSibling as HTMLElement | null
                if (sibling) sibling.style.display = 'inline'
              }}
            />
            <span style={{ display: 'none', fontSize: 13, fontWeight: 800, color: 'hsl(var(--amber))' }}>Construzilla</span>
          </Link>
          <div className="w-8 md:hidden" />
          <h2 className="text-[15px] font-bold tracking-tight text-foreground flex-1 flex items-center gap-2">
            {title}
            {isCliente && (
              <span
                className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: 'hsl(var(--amber-bg))', color: 'hsl(var(--amber))', border: '1px solid hsl(var(--amber) / 0.3)' }}
              >
                Cliente
              </span>
            )}
          </h2>

          {/* Signature notifications */}
          <AssinaturaNotificacoesBadge />

          {/* Notification Bell */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={() => setShowNotifs(prev => !prev)}
              className="relative p-1.5 rounded-lg transition-colors hover:bg-muted"
              aria-label="Notificações"
            >
              <Bell className="w-5 h-5 text-muted-foreground" style={{ opacity: 0.85 }} />
              {totalNotifs > 0 && (
                <span
                  className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold rounded-full text-white"
                  style={{ background: 'hsl(var(--rose))' }}
                >
                  {totalNotifs}
                </span>
              )}
            </button>

            {showNotifs && (
              <div
                className="absolute right-0 top-full mt-2 w-80 rounded-xl shadow-xl border z-50 overflow-hidden"
                style={{
                  background: 'hsl(var(--bg1))',
                  borderColor: 'hsl(var(--line))',
                }}
              >
                <div className="px-4 py-3 font-bold text-sm text-foreground" style={{ borderBottom: '1px solid hsl(var(--line))' }}>
                  Notificações
                </div>
                {notifItems.length === 0 ? (
                  <div className="px-4 py-6 text-center text-sm text-muted-foreground">
                    ✅ Nenhuma notificação pendente
                  </div>
                ) : (
                  <div className="max-h-72 overflow-y-auto">
                    {notifItems.map((item, i) => (
                      <button
                        key={i}
                        onClick={() => { setShowNotifs(false); navigate(item.route) }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors"
                        style={{ borderBottom: i < notifItems.length - 1 ? '1px solid hsl(var(--line) / 0.5)' : undefined }}
                      >
                        <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${item.color}20`, color: item.color }}>
                          {item.icon}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground truncate">{item.label}</div>
                        </div>
                        <span
                          className="flex-shrink-0 min-w-[24px] h-6 flex items-center justify-center text-[11px] font-bold rounded-full text-white px-1.5"
                          style={{ background: item.color }}
                        >
                          {item.count}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-5 md:p-6" style={{ paddingBottom: 'env(safe-area-inset-bottom, 20px)' }}>
          {children}
        </main>
      </div>
      <PwaInstallBanner />
    </div>
  )
}